import axios from "axios";

export interface RejectPayload {
  message: string;
  status?: number;
}

interface ApiErrorResponseBody {
  message?: unknown;
  errors?: unknown;
}

const getStringMessage = (value: unknown): string | undefined => {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

const getNestedErrorMessage = (errors: unknown): string | undefined => {
  if (!errors) return undefined;

  const direct = getStringMessage(errors);
  if (direct) return direct;

  if (Array.isArray(errors)) {
    for (const item of errors) {
      const message = getNestedErrorMessage(item);
      if (message) return message;
    }

    return undefined;
  }

  if (typeof errors === "object") {
    const record = errors as Record<string, unknown>;

    const explicitMessage = getStringMessage(record.message);
    if (explicitMessage) return explicitMessage;

    for (const value of Object.values(record)) {
      const message = getNestedErrorMessage(value);
      if (message) return message;
    }
  }

  return undefined;
};

const getApiMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") {
    return undefined;
  }

  const body = data as ApiErrorResponseBody;

  return getStringMessage(body.message) ?? getNestedErrorMessage(body.errors);
};

export function isRequestCancelled(
  err: unknown,
  signal?: AbortSignal,
): boolean {
  return Boolean(signal?.aborted) || axios.isCancel(err);
}

export function toRejectPayload(
  err: unknown,
  fallbackMessage: string,
): RejectPayload {
  if (axios.isAxiosError(err)) {
    return {
      message:
        getApiMessage(err.response?.data) || err.message || fallbackMessage,
      status: err.response?.status,
    };
  }

  if (err instanceof Error) {
    return { message: err.message || fallbackMessage };
  }

  return { message: fallbackMessage };
}
